// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import ScoreFactor from "./score-factor.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": true
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": true
    },
    "identifier": {
      "column": {
        "default": null,
        "name": "identifier",
        "null": true,
        "type": "string"
      },
      "name": "identifier",
      "selected_by_default": null,
      "translated": false
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": true
    },
    "name": {
      "column": null,
      "name": "name",
      "selected_by_default": false,
      "translated": true
    },
    "name_da": {
      "column": null,
      "name": "name_da",
      "selected_by_default": false,
      "translated": false
    },
    "name_en": {
      "column": null,
      "name": "name_en",
      "selected_by_default": false,
      "translated": false
    },
    "name_no": {
      "column": null,
      "name": "name_no",
      "selected_by_default": false,
      "translated": false
    },
    "name_se": {
      "column": null,
      "name": "name_se",
      "selected_by_default": false,
      "translated": false
    }
  },
  "className": "ScoreFactorGroup",
  "collectionKey": "score_factor_groups",
  "collectionName": "score_factor_groups",
  "i18nKey": "score_factor_group",
  "camelizedLower": "scoreFactorGroup",
  "name": "ScoreFactorGroup",
  "nameDasherized": "score-factor-group",
  "pluralName": "score_factor_groups",
  "ransackable_associations": [
    {
      "className": "ScoreFactorGroup::Audit",
      "collectionName": "score_factor_group_audits",
      "foreignKey": "score_factor_group_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "ScoreFactorGroupAudit",
      "through": null
    },
    {
      "className": "ScoreFactorGroup::Audit",
      "collectionName": "score_factor_group_audits",
      "foreignKey": "score_factor_group_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "ScoreFactorGroupAudit",
      "through": null
    },
    {
      "className": "ScoreFactorGroup::Translation",
      "collectionName": null,
      "foreignKey": "score_factor_group_id",
      "name": "current_translation",
      "macro": "has_one",
      "resource_name": null,
      "through": null
    },
    {
      "className": "SchoolClassCheckInScoreFactorCache",
      "collectionName": null,
      "foreignKey": "score_factor_group_id",
      "name": "school_class_check_in_score_factor_caches",
      "macro": "has_many",
      "resource_name": null,
      "through": null
    },
    {
      "className": "ScoreFactorGroupScoreFactor",
      "collectionName": "score_factor_group_score_factors",
      "foreignKey": "score_factor_group_id",
      "name": "score_factor_group_score_factors",
      "macro": "has_many",
      "resource_name": "ScoreFactorGroupScoreFactor",
      "through": null
    },
    {
      "className": "ScoreFactor",
      "collectionName": "score_factors",
      "foreignKey": "score_factor_id",
      "name": "score_factors",
      "macro": "has_many",
      "resource_name": "ScoreFactor",
      "through": "score_factor_group_score_factors"
    },
    {
      "className": "ScoreFactorGroup::Translation",
      "collectionName": null,
      "foreignKey": "score_factor_group_id",
      "name": "translations",
      "macro": "has_many",
      "resource_name": null,
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "identifier",
      "column": {
        "default": null,
        "name": "identifier",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "ScoreFactor",
      "collectionName": "score_factors",
      "foreignKey": "score_factor_id",
      "name": "score_factors",
      "macro": "has_many",
      "resource_name": "ScoreFactor",
      "through": "score_factor_group_score_factors"
    }
  ],
  "paramKey": "score_factor_group",
  "primaryKey": "id"
}

/** Frontend model for ScoreFactorGroup. */
export default class ScoreFactorGroup extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  identifier() {
    return this.readAttributeUnderscore("identifier")
  }

  /** @returns {boolean} */
  hasIdentifier() {
    const value = this.identifier()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  name() {
    return this.readAttributeUnderscore("name")
  }

  /** @returns {boolean} */
  hasName() {
    const value = this.name()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameDa() {
    return this.readAttributeUnderscore("name_da")
  }

  /** @returns {boolean} */
  hasNameDa() {
    const value = this.nameDa()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameEn() {
    return this.readAttributeUnderscore("name_en")
  }

  /** @returns {boolean} */
  hasNameEn() {
    const value = this.nameEn()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameNo() {
    return this.readAttributeUnderscore("name_no")
  }

  /** @returns {boolean} */
  hasNameNo() {
    const value = this.nameNo()

    return this._isPresent(value)
  }

  /** @returns {any} */
  nameSe() {
    return this.readAttributeUnderscore("name_se")
  }

  /** @returns {boolean} */
  hasNameSe() {
    const value = this.nameSe()

    return this._isPresent(value)
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  average(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (this._callMemberCommand(
      {
        args,
        command: "average",
        primaryKey: this.primaryKey(),
        collectionName: "score_factor_groups",
        type: "member"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  averageOverDays(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (this._callMemberCommand(
      {
        args,
        command: "average_over_days",
        primaryKey: this.primaryKey(),
        collectionName: "score_factor_groups",
        type: "member"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  barChartData(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (this._callMemberCommand(
      {
        args,
        command: "bar_chart_data",
        primaryKey: this.primaryKey(),
        collectionName: "score_factor_groups",
        type: "member"
      },
      commandArgs
    ))
  }

  /**
   * @template TCommandResponse
   * @param {Record<string, any> | HTMLFormElement | FormData} args
   * @param {Record<string, any>} [commandArgs]
   * @returns {Promise<TCommandResponse>}
   */
  tagCloud(args, commandArgs = {}) {
    return /** @type {Promise<TCommandResponse>} */ (this._callMemberCommand(
      {
        args,
        command: "tag_cloud",
        primaryKey: this.primaryKey(),
        collectionName: "score_factor_groups",
        type: "member"
      },
      commandArgs
    ))
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./score-factor.js").default>} */
  scoreFactors() {
    return new Collection(
      {
        reflectionName: "score_factors",
        model: this,
        modelClass: ScoreFactor
      },
      {
        params: {
          through: {
            model: "ScoreFactorGroup",
            id: this.primaryKey(),
            reflection: "score_factors"
          }
        }
      }
    )
  }

  /** @returns {Promise<Array<import("./score-factor.js").default>>} */
  loadScoreFactors() {
    return this._loadHasManyReflection(
      {
        reflectionName: "score_factors",
        model: this,
        modelClass: ScoreFactor
      },
      {
        params: {
          through: {
            model: "ScoreFactorGroup",
            id: this.primaryKey(),
            reflection: "score_factors"
          }
        }
      }
    )
  }

  /** @returns {Promise<Array<import("./score-factor.js").default>>} */
  ensureScoreFactorsLoaded() {
    return this.ensureAssociationLoaded("score_factors")
  }
}
